import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const CART_REQUEST_TIMEOUT = 12000;

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit} init
 */
async function fetchCartWithTimeout(input, init) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), CART_REQUEST_TIMEOUT);

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Cart is taking too long to respond. Please try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 1200);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} addToCartErrorMessage - The add to cart error message text.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {boolean} */
  #waitingForVariant = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    target?.addEventListener('vorliva:variant-intent', this.#onVariantIntent, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    window.addEventListener('pagehide', this.#resetAfterNavigation, { signal });
    window.addEventListener('pageshow', this.#resetAfterNavigation, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#isSubmitting) return;

    if (this.#variantChangeInProgress && this.dataset.variantIntentReady !== 'true') {
      const quantity = this.#getQuantity();

      // Keep one intent only. Rapid clicks while a color request is resolving
      // must never add duplicates or submit the previously selected color.
      this.#addToCartQueue = [{ variantId: '', quantity }];
      this.#waitingForVariant = true;
      this.#setSubmitting(true);
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * Lets another purchase control, such as Continue to checkout, share the
   * same lock as the main and sticky add-to-cart buttons.
   * @returns {boolean} Whether the lock was acquired.
   */
  lockPurchase() {
    if (this.#isSubmitting) return false;
    this.#setSubmitting(true);
    return true;
  }

  unlockPurchase() {
    this.#setSubmitting(false);
  }

  #resetAfterNavigation = () => {
    this.#addToCartQueue = [];
    this.#waitingForVariant = false;
    this.#setSubmitting(false);
  };

  /** @param {CustomEvent} event */
  #onVariantIntent = (event) => {
    const detail = event.detail ?? {};
    if (detail.productId && detail.productId !== this.dataset.productId) return;

    const variantId = String(detail.variantId ?? '');
    this.refs.variantId.value = variantId;
    this.dataset.variantIntentReady = variantId ? 'true' : 'false';

    const available = Boolean(variantId) && detail.available !== false;
    const addToCartContainer = this.refs.addToCartButtonContainer;
    if (available) addToCartContainer?.enable();
    else addToCartContainer?.disable();
    const addButton = addToCartContainer?.refs.addToCartButton;
    if (addButton) addButton.dataset.disabledAfterSubmit = String(!available);

    if (this.refs.acceleratedCheckoutButtonContainer) {
      this.refs.acceleratedCheckoutButtonContainer.toggleAttribute('hidden', !available);
    }

    if (detail.mediaUrl && addToCartContainer) {
      addToCartContainer.setAttribute('data-product-variant-media', String(detail.mediaUrl));
    }

    // A section morph must not unlock an add request that is already running.
    if (this.#isSubmitting) this.#setSubmitting(true);
  };

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Reads the authoritative item count from Shopify's bundled cart-drawer
   * section. The section is rendered in the same response as /cart/add.js,
   * so the UI does not need to wait for a second /cart.js request.
   *
   * @param {Record<string, string | null> | undefined} sections
   * @param {number} addedQuantity
   */
  #getBundledCartCount(sections, addedQuantity) {
    for (const sectionHtml of Object.values(sections ?? {})) {
      if (!sectionHtml) continue;

      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const badge = html.querySelector('.theme-drawer__badge');
      const count = Number(badge?.textContent?.trim());
      if (Number.isFinite(count)) return count;
    }

    const visibleCount = Number(document.querySelector('[ref="cartBubbleCount"]')?.textContent?.trim()) || 0;
    return visibleCount + addedQuantity;
  }

  /**
   * Resolves the standard cart event immediately from bundled section HTML,
   * then refreshes the complete cart model in the background for quantity
   * rules and checkout integrations.
   *
   * @param {object} params
   * @param {any} params.response
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} params.deferredEventPromise
   * @param {number} params.addedQuantity
   * @param {string} params.variantId
   * @param {NodeListOf<AddToCartComponent>} params.addToCartContainers
   */
  #completeSuccessfulAdd({ response, deferredEventPromise, addedQuantity, variantId, addToCartContainers }) {
    const totalItemCount = this.#getBundledCartCount(response.sections, addedQuantity);

    deferredEventPromise.resolve({
      cart: null,
      detail: {
        source: 'product-form-component',
        sourceId: this.id.toString(),
        itemCount: totalItemCount,
        productId: this.dataset.productId,
        sections: response.sections,
        didError: false,
      },
    });

    for (const container of addToCartContainers) {
      container.animateAddToCart?.();
    }

    const addedText = Theme.translations.added;
    this.#setLiveRegionText(addedText);
    setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);

    // On the main product page, continue the user's purchase intent by opening
    // Addressly after the cart has accepted the item. Quick-add cards and modal
    // previews keep their normal cart behaviour so browsing is not interrupted.
    const main = document.querySelector('main[data-template]');
    const isProductPage = main?.getAttribute('data-template')?.split('.')[0] === 'product';
    const isQuickAdd = Boolean(this.closest('product-card, .quick-add-modal, quick-add-modal'));

    this.dispatchEvent(
      new CustomEvent('vorliva:cart-item-added', {
        bubbles: true,
        composed: true,
        detail: {
          variantId,
          productId: this.dataset.productId ?? '',
          quantity: addedQuantity,
          autoOpenAddressAssist: isProductPage && !isQuickAdd,
        },
      })
    );

    // This follow-up request no longer blocks the button, cart count, section
    // render, or drawer opening. It only supplies the complete cart model to
    // controls that need line-level quantities.
    this.#refreshCart()
      .then((cart) => {
        if (this.#getVariantIdInput()) this.#updateCartQuantity(cart);
        document.dispatchEvent(
          new CustomEvent('vorliva:cart-ready', {
            detail: { cart, variantId },
          })
        );
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Background cart sync failed:', error);
      });
  }

  /**
   * Settles the standard event after a rejected cart add without causing a
   * false cart-count animation or opening the drawer.
   *
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredEventPromise
   * @param {number} attemptedQuantity
   */
  #completeFailedAdd(deferredEventPromise, attemptedQuantity) {
    deferredEventPromise.resolve({
      cart: null,
      detail: {
        didError: true,
        source: 'product-form-component',
        sourceId: this.id.toString(),
        itemCount: attemptedQuantity,
        productId: this.dataset.productId,
      },
    });
  }

  /** @param {string} message */
  #showAddError(message) {
    const { addToCartTextError, addToCartErrorMessage } = this.refs;
    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');
    if (addToCartErrorMessage) addToCartErrorMessage.textContent = message;
    this.#setLiveRegionText(message);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #hideAddError() {
    this.refs.addToCartTextError?.classList.add('hidden');
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        this.#showAddError(errorMessage);

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = /** @type {string[]} */ ([]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.set('sections', [...new Set(cartItemComponentsSectionIds)].join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: /** @type {string} */ (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    this.#setSubmitting(true);

    const addRequest = fetchCartWithTimeout(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok && !payload?.status) {
          payload.status = response.status;
          payload.message ||= 'Add to cart failed';
        }
        return payload;
      })
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#completeFailedAdd(deferredEventPromise, itemCount);

          this.#showAddError(response.message);
          return;
        } else {
          const id = formData.get('id');

          this.#hideAddError();

          if (!id) throw new Error('Form ID is required');

          this.#completeSuccessfulAdd({
            response,
            deferredEventPromise,
            addedQuantity: itemCount,
            variantId: id.toString(),
            addToCartContainers: allAddToCartContainers,
          });
        }
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        const errorMessage = error?.message || Theme.translations.add_to_cart_error || 'Error adding to cart';
        this.#showAddError(errorMessage);

        this.dispatchEvent(
          new CartErrorEvent({
            error: errorMessage,
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#setSubmitting(false);
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });

    window.VorlivaCartMutations?.track(addRequest);
  }

  /** @param {boolean} busy */
  #setSubmitting(busy) {
    this.#isSubmitting = busy;
    this.toggleAttribute('aria-busy', busy);
    this.dataset.purchasePending = busy ? 'true' : 'false';
    this.dispatchEvent(
      new CustomEvent('vorliva:purchase-lock', {
        bubbles: true,
        composed: true,
        detail: { busy, productId: this.dataset.productId ?? '' },
      })
    );

    const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (
      this.querySelectorAll('add-to-cart-component [ref="addToCartButton"]')
    );

    for (const button of buttons) {
      if (busy) {
        if (button.dataset.wasDisabledBeforeSubmit === undefined) {
          button.dataset.wasDisabledBeforeSubmit = String(button.disabled);
        }
        if (button.matches('add-to-cart-component [ref="addToCartButton"]')) {
          button.dataset.loading = 'true';
        }
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      } else {
        if (button.dataset.disabledAfterSubmit !== undefined) {
          button.disabled = button.dataset.disabledAfterSubmit === 'true';
        } else if (button.dataset.wasDisabledBeforeSubmit === 'false') {
          button.disabled = false;
        }
        delete button.dataset.wasDisabledBeforeSubmit;
        delete button.dataset.disabledAfterSubmit;
        button.removeAttribute('data-loading');
        button.removeAttribute('aria-busy');
      }
    }
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: [...new Set(cartItemComponentsSectionIds)].join(','),
      sections_url: window.location.pathname,
    };

    this.#setSubmitting(true);

    const batchRequest = fetchCartWithTimeout(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        const result = await response.json();
        if (!response.ok && !result?.status) {
          result.status = response.status;
          result.message ||= 'Add to cart failed';
        }
        return result;
      })
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#completeFailedAdd(deferredEventPromise, totalQuantity);

          this.#showAddError(response.message);
          return;
        }

        this.#hideAddError();

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        this.#completeSuccessfulAdd({
          response,
          deferredEventPromise,
          addedQuantity: totalQuantity,
          variantId: items[items.length - 1]?.variantId ?? '',
          addToCartContainers: allAddToCartContainers,
        });
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);

        const errorMessage = error?.message || Theme.translations.add_to_cart_error || 'Error adding to cart';
        this.#showAddError(errorMessage);

        this.dispatchEvent(
          new CartErrorEvent({
            error: errorMessage,
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => this.#setSubmitting(false));

    window.VorlivaCartMutations?.track(batchRequest);
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;

    let variantResolved = false;

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';
      this.dataset.variantIntentReady = resource?.id ? 'true' : 'false';
      variantResolved = Boolean(resource?.id);

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        if (newProduct) morph(currentAddToCartButton, newAddToCartButton);
        else morph(currentAddToCartButton, newAddToCartButton, { childrenOnly: true });
        if (this.#isSubmitting) this.#setSubmitting(true);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[product-form] Variant update failed:', error);
      }
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;
        delete this.dataset.variantIntentReady;

        // Product option responses may morph either the button or the whole
        // button group. Re-assert the purchase lock after every morph.
        if (this.#isSubmitting) this.#setSubmitting(true);

        // Drain any queued add-to-cart requests that accumulated during the variant change
        if (variantResolved && this.#addToCartQueue.length > 0) {
          const queuedItems = [...this.#addToCartQueue];
          this.#addToCartQueue = [];
          this.#waitingForVariant = false;
          const currentVariantId = this.refs.variantId?.value;
          this.#setSubmitting(false);
          if (currentVariantId) {
            this.#processBatchAddToCart(
              queuedItems.map((item) => ({
                ...item,
                variantId: currentVariantId,
              }))
            );
          }
        } else if (!variantResolved) {
          this.#addToCartQueue = [];
          if (this.#waitingForVariant) {
            this.#waitingForVariant = false;
            this.#setSubmitting(false);
          }
        }
      }
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
